import type { CameraTimelineOperation } from "../../../../platform/cameraPath/common/cameraTimelineOperations.js";
import { Emitter, Event } from "../../../../base/common/events";
import { BUILD_ENVIRONMENT, BUILD_PLATFORM } from "../../../../base/common/$__metadata";
import { Disposable } from "../../../../base/common/lifecycle";
// LOCAL binding on purpose: the `export {} from` re-export below does NOT
// create one, and code in THIS module referencing the identifier threw
// ReferenceError at runtime (the same trap the rehydrate opener hit)
import { IOperationService } from "./operationsIdentifier.js";
import { createDecorator } from "../../../../platform/instantiation/common/registry";
import { ServicesAccessor } from "../../../../platform/instantiation/common/instantiation";
import { ILogService } from "../../../../platform/log/common/log";
// type-only ON PURPOSE: a value import would close the runtime cycle
// operations → properties → propertyCommands → changeset → operations
// (TDZ crash on the changeset's decorators)
import type { IProperty, PropertyMetadata } from "../../../../platform/properties/common/properties";
import { IObjectTargetService, type OverTarget } from "../../project/common/documentTarget";

/**
 * The editor's mutation/operation seam (plans/project-format-v0.md, structural-operation
 * capture). The operation is the PRIMITIVE — undo, the document fold, and (future)
 * networking are all filtered CONSUMERS of it, not the source. This corrects the
 * "route mutations through undo/redo" framing: undo is one consumer, below it is this.
 *
 * Stage 1 has one op kind (set-property) with property edits as the only producer, to
 * prove the seam with no new behavior. Structural op kinds (add-reference, add-component,
 * assign-material, remove-node) and their producers are added on this seam later; each
 * new producer emits one Operation instead of wiring itself to every consumer (N+M, not
 * N×M).
 */

export type OperationOrigin = "local-user" | "ai" | "remote";

/**
 * HARD RULE (plans/operation-bridge.md): operations address their targets by
 * ADDRESS, never by live object reference — a local edit's op is
 * indistinguishable from one that arrived over the wire or from AI. This
 * helper is the ONE producer-side conversion: live object → {ref, address}.
 *
 * Null = this object is not addressable HERE: either the product has no
 * addressing (the chrome extension until it writes the document format —
 * a capability boundary, its ops feed nothing structural today), or the
 * object is unstamped/foreign (logged loud — a stamping hole, the same
 * class as a provenance miss). Callers SKIP emission on null; they never
 * invent a fallback identity.
 */
export function opTargetFor(accessor: ServicesAccessor, object: object): OverTarget | null {
    const targets = accessor.getIfExists(IObjectTargetService) as IObjectTargetService | undefined;
    const operations = accessor.getIfExists(IOperationService) as IOperationService | undefined;
    // H4: an ADDRESSING product without the op service is a wiring hole — every
    // emit site would silently skip. Fail at first use in dev instead.
    if (targets && !operations && captureMissDisposition(BUILD_ENVIRONMENT, BUILD_PLATFORM) === "throw") {
        throw new Error("Operations service missing in an addressing product — emit paths would silently skip (H4, plans/operation-bridge.md).");
    }
    return opTargetVia(
        targets,
        accessor.getIfExists(ILogService) as ILogService | undefined,
        object,
        operations,
    );
}

/** the same conversion for helpers that resolved their services BEFORE an
 * await (the accessor dies at the first one) — one logic body, two entries.
 * `operations` routes a miss through the H4 capture-miss channel (journal +
 * dev-throw); without it the miss only warns — pass it wherever it exists. */
export function opTargetVia(targets: IObjectTargetService | undefined, logger: ILogService | undefined, object: object, operations?: Pick<IOperationService, "reportCaptureMiss">): OverTarget | null {
    if (!targets) return null; // product without addressing (chrome) — see above
    const target = targets.targetFor(object);
    if (!target) {
        const name = (object as { name?: string }).name || "object";
        logger?.warn(`Operations: '${name}' is not addressable — the edit is NOT captured as an operation (stamping hole?)`);
        operations?.reportCaptureMiss({ kind: "emit-target", reason: `'${name}' is not addressable (stamping hole?)`, detail: { name } });
    }
    return target;
}

// #region Capture misses (H4, plans/operation-bridge.md hardening)

/** an edit that happened LIVE but never became an op — no sync, no undo, no
 * save. Reported through {@link IOperationService.reportCaptureMiss} so the
 * hole is visible in the journal of EVERY product, and dev builds of the
 * addressing product fail loudly instead of degrading to a warn. */
export interface CaptureMiss {
    /** what kind of edit dropped ("set-property", "assign-material", "emit-target", …) */
    readonly kind: string;
    readonly reason: string;
    /** small plain data only — names/paths, never live objects */
    readonly detail?: Record<string, unknown>;
}

/** warn-and-skip stays CORRECT for chrome (no addressing until #38) and for
 * production (a user session must survive a stamping hole); a DEV build of
 * the addressing product throws — bypassing the seam must FAIL, not degrade */
export function captureMissDisposition(environment: string, platform: string): "throw" | "warn" {
    return environment === "dev" && platform === "web-editor" ? "throw" : "warn";
}

const CAPTURE_MISS_ERROR = "CaptureMissError";

/** defensive catches around op emission MUST rethrow these — swallowing one
 * degrades the dev-throw back to exactly the warn H4 exists to prevent */
export function isCaptureMissError(error: unknown): boolean {
    return error instanceof Error && error.name === CAPTURE_MISS_ERROR;
}

// #endregion

/** THE place command emission points derive origin from the command context — extend
 * here (not per command) when remote networking adds its marker */
export function originFromContext(context?: { isAi?: boolean }): OperationOrigin {
    return context?.isAi ? "ai" : "local-user";
}

/**
 * The minimum an operation needs to describe an edited property. `IProperty`
 * satisfies this structurally, so inspector edits keep passing their real
 * property — but an edit that never came from a property row (a transform-gizmo
 * drag; animation clip/mixer edits next) can now describe itself HONESTLY
 * instead of casting a partial object to the full `IProperty`, which crashed
 * export the moment a consumer called a method that wasn't there.
 *
 * `equalsValue` / `cloneValue` are optional: consumers must fall back (see
 * {@link operationValuesEqual}) rather than assume an inspector property.
 */
export interface IOperationProperty {
    /** property name as the document/consumers address it ("position", "color") */
    readonly key: string;
    /** the object the value lives on — how consumers attribute the edit */
    readonly owner: object;
    /** value-type hint ("color", "number", …) — consumers format by it */
    readonly type?: string;
    /** optional hints; `metadata.address` drives deep-path attribution in the fold,
     *  `enumOptions` gives the changeset human labels */
    readonly metadata?: PropertyMetadata;
    /** current value, read live at consume/save time */
    readonly value: unknown;
    equalsValue?(value: unknown, oldValue?: unknown): boolean;
    cloneValue?(): unknown;
}

/**
 * Compare two operation values, preferring the property's own comparison and
 * falling back to a structural one — an op property need not provide it.
 */
export function operationValuesEqual(property: IOperationProperty, a: unknown, b: unknown): boolean {
    if (typeof property.equalsValue === "function") return property.equalsValue(a, b);
    if (Object.is(a, b)) return true;
    const toArray = (value: unknown): number[] | null => {
        if (Array.isArray(value)) return value as number[];
        const maybe = value as { toArray?: () => number[] } | null | undefined;
        return typeof maybe?.toArray === "function" ? maybe.toArray() : null;
    };
    const left = toArray(a);
    const right = toArray(b);
    if (left && right) return left.length === right.length && left.every((v, i) => Math.abs(v - right[i]) <= 1e-9);
    return false;
}

/**
 * A property value edit — address-only (plans/operation-bridge.md 1f): the owner is
 * an {@link OverTarget} (deep edits carry the ANCHOR's address + the full document
 * path; component fields ride the target's `pathPrefix`), and both values are
 * SNAPSHOTS taken at emit (cloneValue) — the save serializes what the op carries,
 * never the live scene. The op is self-contained: the same payload folds
 * identically whether it was made here, by AI, or arrived over a wire.
 */
export interface SetPropertyOperation {
    readonly kind: "set-property";
    /** the edited element's address — node, component (pathPrefix), material,
     * reference root (address-less), or the reserved $scene composition root */
    readonly target: OverTarget;
    /** property path FROM the target — "position", "material/map/repeat", … */
    readonly path: string;
    /** the value at emit, snapshotted — never a live-updating reference */
    readonly value: unknown;
    readonly previousValue: unknown;
}

/**
 * Assign a material to an object. Two sources:
 * - ASSET-sourced (library/PolyHaven material glb): `assetSrc` set — becomes an
 *   `assets` entry + a reference-valued over on the object's `material`.
 * - SCENE-sourced (a material picked from the live scene — the first wired type of
 *   the generic REFERENCE-VALUED over rail, plans/project-format-v0.md 2026-07-28):
 *   `assetSrc` ABSENT. The material's HOME FILE and address id are resolved at SAVE
 *   time, not here — the material may only become file-backed by the same save's
 *   content rebake (paste → edit material → assign → save). The fold re-resolves
 *   the live material from the target's slot at op time (the command has already
 *   set it when the op is emitted), so the payload stays address-only.
 * The producer computes the material's address inside the asset (deterministic id from
 * type+name+occurrence, so it works for un-stamped/external assets).
 */
export interface AssignMaterialOperation {
    readonly kind: "assign-material";
    /** the address of the object whose material is being set */
    readonly target: OverTarget;
    /** the material asset's source uri — becomes an `assets` entry (deduped by src);
     * absent for scene-sourced assigns (home resolved at save) */
    readonly assetSrc?: string;
    /** friendly key hint for the assets-map entry (falls back to a name derived from src) */
    readonly assetName?: string;
    /**
     * asset identity (provider + id) recorded alongside `src` in the document, so the opener
     * can recover the SAME asset-service resolver at reopen (external/provider textures resolve
     * at load time). Absent for assets with no provider identity (plain-src, self-contained).
     */
    readonly assetIdentity?: { readonly id: string; readonly provider?: string };
    /** the material's address inside the asset (id computed by the producer; name fallback) */
    readonly material: { readonly id: string; readonly name?: string };
}

/**
 * Assign an ENVIRONMENT (HDRI) asset to the scene — sets `$scene.background` +
 * `$scene.environment`. The fold turns this into a whole-asset entity reference
 * (`address: { type: "hdr" }`) + `{ref}` overs on the reserved `$scene` target.
 */
export interface AssignEnvironmentOperation {
    readonly kind: "assign-environment";
    /** Addressed scene whose slots are changed. */
    readonly target: OverTarget;
    /** the chosen version's uri — becomes the `assets` entry (deduped by src) */
    readonly assetSrc: string;
    /** friendly key hint for the assets-map entry */
    readonly assetName?: string;
    /** provider identity (see AssignMaterialOperation.assetIdentity) */
    readonly assetIdentity?: { readonly id: string; readonly provider?: string };
    /** which scene slots this assign sets — default BOTH (the scene-wide apply); the
     * property quickpick sets a single slot */
    readonly paths?: readonly ("background" | "environment")[];
}

/**
 * A node ADDED to the hierarchy this session (create-primitive, duplicate, glTF load).
 * The node is SCRATCH until content birth captures it at save — the fold records no
 * structural entry for it yet (scratch consolidation will); today the op exists so
 * AUTHORING marks the session dirty like every other edit (without it the
 * unsaved-changes guard silently discarded freshly created content). Producers emit
 * `delete-node` on undo and `restore-node` on redo — before a save those attribute to
 * nothing (scratch), after a save they correctly become / net out an `active:false`
 * over on the captured content.
 */
export interface AddNodeOperation {
    readonly kind: "add-node";
    /** Document opinions captured and retargeted when this node was duplicated. */
    readonly document?: import("../../project/common/duplicateDocumentOpinions.js").CreatedDocumentFragment;
    /** the added node's ADDRESS — user creates are stamped before emission
     * (id-at-create), so the op CARRIES the minted id: self-contained, the
     * same op can materialize the node on a peer (birth ordering) */
    readonly target: OverTarget;
    /** HOW the node was made (no-inline-glb rule): the
     * creating FACTORY's id + its serializable params + the initial state the
     * create set without ops (name, TRS). A peer re-runs the SAME factory
     * (creationFactories registry) and stamps the op-carried id — bytes never
     * travel for something a factory can rebuild. Absent for reference
     * placements (those carry file identity instead). */
    readonly recipe?: CreationRecipe;
}

/** how a created node is rebuilt on a peer — the factory id is the creating
 * command's id, registered in the creationFactories registry */
export interface CreationRecipe {
    readonly factory: string;
    /** the factory's own SERIALIZABLE params (never live objects) */
    readonly params?: unknown;
    /** initial state the create applied without emitting ops */
    readonly state?: { readonly name?: string; readonly position?: number[]; readonly quaternion?: number[]; readonly scale?: number[] };
}

/**
 * Delete a node from the hierarchy. The fold classifies at op time: deleting a placed
 * REFERENCE ROOT drops the whole reference from the document (never loads again);
 * deleting a node INSIDE a loaded asset becomes an `active: false` over (USD semantics —
 * the document does not own the asset's content, it can only deactivate it).
 */
export interface DeleteNodeOperation {
    readonly kind: "delete-node";
    /** the deleted node's address */
    readonly target: OverTarget;
}

/**
 * Add a COMPONENT to a node (the defs arc's capture half). The component's live state is
 * serialized at SAVE time (field edits between add and save are included in the def);
 * the fold records the instance + guid so field-edit overs (guid selector) match.
 */
export interface AddComponentOperation {
    readonly kind: "add-component";
    /** the host node's address */
    readonly target: OverTarget;
    /** TypeStore name — the def wire entry's `name` */
    readonly componentName: string;
    /** the component's guid (stamped at add) — THE component address on its
     * host; the live instance re-resolves from target + guid where
     * save-time serialization needs it */
    readonly guid: string;
}

/**
 * Remove a component. A session-ADDED component nets out (its def never saves); a
 * component from a reopened document's defs drops out of that def; a FILE-owned
 * component cannot be expressed yet and is surfaced in `skipped` at save.
 */
export interface RemoveComponentOperation {
    readonly kind: "remove-component";
    /** the host node's address */
    readonly target: OverTarget;
    /** the component's identity on its host — required: an unidentified
     * component cannot be addressed, so its removal cannot be an op */
    readonly guid: string;
    /** display name for surfaced skips/journal — informational only */
    readonly componentName?: string;
}

/**
 * Move a node to a new parent (drag-drop reparent). The document does not capture the
 * move as a record: reference PLACEMENT (parent mount + TRS) is derived from the LIVE
 * graph at save (same rule as placement overs), so the op's job is dirty-marking and
 * giving cross-cutting consumers (sync, capture) the one emission point. The command
 * REJECTS moves the document cannot express (nodes inside a loaded asset's subtree).
 */
export interface ReparentNodeOperation {
    readonly kind: "reparent-node";
    /** the moved node's address */
    readonly target: OverTarget;
    /** parents as addresses — the composition root is the reserved
     * { ref: "$scene" }; null = detached */
    readonly oldParent: OverTarget | null;
    readonly newParent: OverTarget | null;
}

/**
 * Move/rename a PROJECT FILE (plans/file-identity-and-text-editing.md, step 2).
 * The file is addressed by its registry GUID — never by path, which is pure
 * location: the op is what keeps assignments alive across the move (the asset
 * entry's guid stays, only `src` follows). Paths are folder-relative. The
 * apply moves the physical file, the registry row and re-homes any open-file
 * entry; the document's asset srcs follow at save (writer-side heal).
 */
export interface MoveFileOperation {
    readonly kind: "move-file";
    /** the file's stable identity — the file registry's guid */
    readonly file: string;
    /** folder-relative destination path */
    readonly path: string;
    /** folder-relative origin — the undo/remote-undo anchor (reparent's
     * oldParent precedent: retraction moves the file back here) */
    readonly oldPath: string;
}

/**
 * Create a PROJECT FILE (plans/file-identity-and-text-editing.md, step 3).
 * Id-at-create like add-node: the op CARRIES the minted guid, so a peer
 * materializes the same identity (received, never re-minted). v0 carries TEXT
 * content inline — small by construction (the command refuses oversized
 * content); binary files enter through the import flow, and the planned
 * `blobHash` variant rides the cloud blob pool when that slice lands.
 */
export interface CreateFileOperation {
    readonly kind: "create-file";
    /** the minted file guid — identity from birth */
    readonly file: string;
    /** folder-relative path */
    readonly path: string;
    /** utf-8 text content ("" = empty file) */
    readonly content: string;
}

/**
 * Delete a PROJECT FILE. Undo = retraction restores the file: the deleting
 * side re-writes its captured bytes and re-seeds the SAME guid; remote
 * appliers retain the deleted bytes for the peer's undo (the delete-node
 * retain precedent). `path` rides along for journal/restore — identity is
 * the guid.
 */
export interface DeleteFileOperation {
    readonly kind: "delete-file";
    readonly file: string;
    /** folder-relative path at delete time */
    readonly path: string;
}

/**
 * Edit a TEXT FILE's content (plans/file-identity-and-text-editing.md, step
 * 4 — the OT rail). Deltas only: positional edits in monaco's change shape,
 * expressed against the FILE REVISION the author had incorporated when
 * emitting — receivers forward-transform lagging ops across the canonical
 * revision stream (textSyncClient.ts), which the room's total order defines.
 * Whole-file content never rides an op.
 *
 * UNDO EXCEPTION (decided in the plan): edit-file undo EMITS the transformed
 * inverse as a NEW op instead of retracting — text state is not folded from
 * the log, so a retraction has nothing to replay. Remote retract/reinstate of
 * an edit-file op are explicit no-ops.
 */
export interface EditFileOperation {
    readonly kind: "edit-file";
    /** the file's stable id (registry guid) */
    readonly file: string;
    /** sorted, non-overlapping edits against the base revision's text */
    readonly edits: readonly { readonly offset: number; readonly deleted: number; readonly inserted: string }[];
    /** the canonical file revision these edits are expressed against */
    readonly baseRevision: number;
}

/** the op payload union — grows with structural op kinds */
export type OperationPayload = CameraTimelineOperation | SetPropertyOperation | AssignMaterialOperation | AssignEnvironmentOperation | AddNodeOperation | DeleteNodeOperation | AddComponentOperation | RemoveComponentOperation | ReparentNodeOperation | MoveFileOperation | CreateFileOperation | DeleteFileOperation | EditFileOperation;

export type Operation = OperationPayload & {
    /** unique within this session (actor+counter later, for networking dedup) */
    readonly id: string;
    /** monotonic per-session order — "which edit came later". Consumers that combine
     * edits of different kinds (the document fold/writer) use this so the SAVED result
     * follows the order the user actually did things, not bucket order. */
    readonly sequence: number;
    /** who produced it — consumers filter on this (undo: local only; sync: not remote) */
    readonly origin: OperationOrigin;
    /** the SENDING client's own origin, preserved across ingest (which rewrites
     *  `origin` to "remote"). Attribution reads this — an AI edit made in
     *  another client is still the AI, not "a collaborator". Absent on
     *  locally-emitted ops, where `origin` already says it. */
    readonly authoredBy?: OperationOrigin;
};

// the identifier lives in a LEAF module (operationsIdentifier.ts) so the
// UndoableCommand base can resolve the service without importing this module
// (runtime cycle); re-exported here so consumers keep this import path
export { IOperationService } from "./operationsIdentifier.js";

// #region op-kind exhaustiveness (H2, plans/operation-bridge.md System hardening)

/** EVERY op kind, runtime-enumerable (the coverage test iterates this).
 * `satisfies` locks it against inventing kinds; `_missing` below locks the
 * other direction — a kind added to the union must join this list. */
export const OPERATION_KINDS = [
    "edit-camera-timeline",
    "set-property",
    "assign-material",
    "assign-environment",
    "add-node",
    "delete-node",
    "add-component",
    "remove-component",
    "reparent-node",
    "move-file",
    "create-file",
    "delete-file",
    "edit-file",
] as const satisfies readonly Operation["kind"][];

type _MissingKinds = Exclude<Operation["kind"], typeof OPERATION_KINDS[number]>;
// becomes a tuple type naming the missing kinds when the list falls behind
const _kindsComplete: [_MissingKinds] extends [never] ? true : ["OPERATION_KINDS is missing:", _MissingKinds] = true;
void _kindsComplete;

/**
 * The H2 default branch: every switch over op kinds ends here instead of a
 * silent `default` — a NEW kind is a compile error at the call site (`op` no
 * longer narrows to never) AND a loud throw if it reaches here at runtime.
 * Kinds a consumer deliberately does not handle get an EXPLICIT case with a
 * named rationale, never a fallthrough.
 */
export function assertUnhandledOperation(op: never, where: string): never {
    throw new Error(`Unhandled operation kind "${(op as Operation).kind}" in ${where} — every kind is explicitly handled or exempted (H2, plans/operation-bridge.md)`);
}

// #endregion

export interface IOperationService {
    readonly _serviceBrand: undefined;

    /** fan-out: undo / document-fold / (future) sync subscribe here */
    readonly onDidEmitOperation: Event<Operation>;

    /** ops left or re-entered the ACTIVE log (undo/redo) — consumers RE-FOLD.
     * Separate from onDidEmitOperation on purpose: emitting is "something new
     * happened", changing the log is "history itself is different now". Carries
     * the OPERATIONS (not just ids): retracted ops are absent from `log`, so a
     * consumer that reacts per kind (e.g. the inspector rebuilding on a material
     * retraction) could not look them up otherwise. */
    readonly onDidChangeLog: Event<{ readonly retracted: readonly Operation[]; readonly reinstated: readonly Operation[] }>;

    /** the ACTIVE operations of this session, in the order they were performed —
     * the fold's input. Retracted ops are absent; reinstated ones are back at
     * their original position (see {@link IOperationService.reinstate}). */
    readonly log: readonly Operation[];

    /** producers emit here — property edits (Stage 1), structural ops (later) */
    emit(payload: OperationPayload, origin?: OperationOrigin): Operation;

    /**
     * A WIRE-RECEIVED operation enters the log with its EXISTING identity
     * (plans/operation-bridge.md): the id is NEVER re-minted — that is what
     * suppresses echo (the transport skips ids it received) and keeps peers
     * folding the same op set. The op gets a LOCAL sequence (v0 arrival order;
     * server-assigned total order replaces this with real transport) and fires
     * the same {@link onDidEmitOperation} every consumer already rides.
     * Duplicate ids are ignored (idempotent per the dedupe contract) — the
     * already-ingested op is returned.
     */
    ingest(op: Operation): Operation;

    /**
     * Take operations back OUT of the log — the model behind UNDO
     * (plans/operation-log-retraction.md): an undone edit did not "happen and get
     * corrected", it did not happen. Consumers re-fold and the document is compiled
     * as if the ops were never emitted.
     *
     * Unknown or already-retracted ids are ignored (a remote op we never folded, a
     * double undo) — no event, no throw.
     */
    retract(ids: readonly string[]): void;

    /** Put retracted operations back as the NEWEST edits (REDO = re-assertion,
     * plans/operation-bridge.md § Undo/redo unification): each op gets a fresh
     * HLC stamp and moves to the log tail, so the live scene, every peer and
     * the fold agree the redone value is the latest — the old keep-original-
     * position rule made a save between redo and undo persist the WRONG value.
     * `restamps` carries a peer's already-minted stamps (applied verbatim, so
     * all windows renumber identically); local redo mints its own. */
    reinstate(ids: readonly string[], restamps?: Readonly<Record<string, number>>): void;

    /** EVERY op of the session in emission order, RETRACTED included — `log` is
     * the active view of this. Catch-up serving reads it paired with
     * {@link isRetracted}. */
    readonly history: readonly Operation[];

    isRetracted(id: string): boolean;

    /**
     * A peer's RETRACTED op enters the history + retracted pool directly — no
     * events, no fold, no live effect (a retracted op contributes nothing until
     * reinstated). Parked so a later wire {@link reinstate} lands at the right
     * position instead of referencing an unknown id (peer catch-up).
     */
    ingestRetracted(op: Operation): void;

    /** H4: an edit happened live but its capture dropped (unaddressable target,
     * inexpressible value) — journals in every product; dev builds of the
     * addressing product THROW after journaling. */
    reportCaptureMiss(miss: CaptureMiss): void;

    /** the journal subscribes here — the debug export must show the hole */
    readonly onDidReportCaptureMiss: Event<CaptureMiss>;
}

/** LWW comparator for op stamps: stamp order, op id as the deterministic
 * tiebreak — two windows CAN mint the same stamp in the same millisecond, and
 * a tie decided by per-window log order diverged the fold across peers */
export function outranks(a: Operation, b: Operation): boolean {
    return a.sequence !== b.sequence ? a.sequence > b.sequence : a.id > b.id;
}

export class OperationService extends Disposable implements IOperationService {
    readonly _serviceBrand: undefined;

    /** override for tests — production resolves from the build flags */
    constructor(private readonly missDisposition: "throw" | "warn" = captureMissDisposition(BUILD_ENVIRONMENT, BUILD_PLATFORM)) {
        super();
    }

    private readonly _onDidEmitOperation = this._register(new Emitter<Operation>());
    readonly onDidEmitOperation = this._onDidEmitOperation.event;

    private readonly _onDidReportCaptureMiss = this._register(new Emitter<CaptureMiss>());
    readonly onDidReportCaptureMiss = this._onDidReportCaptureMiss.event;

    reportCaptureMiss(miss: CaptureMiss): void {
        // journal first, throw after — the record must survive the failure
        this._onDidReportCaptureMiss.fire(miss);
        if (this.missDisposition === "throw") {
            const error = new Error(`Capture miss (${miss.kind}): ${miss.reason} — the edit is NOT an operation (no sync, no undo, no save). H4: fix the emit path instead of degrading to a warn.`);
            error.name = CAPTURE_MISS_ERROR;
            throw error;
        }
    }

    private readonly _onDidChangeLog = this._register(new Emitter<{ readonly retracted: readonly Operation[]; readonly reinstated: readonly Operation[] }>());
    readonly onDidChangeLog = this._onDidChangeLog.event;

    // HLC (hybrid logical clock): stamps are `ms * 1024 + counter` — humanly
    // time-ordered, monotonic per window, and causally bumped past everything
    // observed (an edit made in RESPONSE to an observed edit must outrank it;
    // raw wall-clock cannot promise that across machines). Safe integers until
    // ~year 2248. The old per-window counter made LWW arrival-order luck.
    private _lastStamp = 0;
    /** window-unique id prefix: the old `op-<n>` collided ACROSS windows —
     * concurrent emitters minted the same id and ingest silently dropped the
     * peer's op as a "duplicate" */
    private readonly _idNonce = Math.random().toString(36).slice(2, 8);
    private _idCounter = 0;

    private stamp(): number {
        const now = Date.now() * 1024;
        this._lastStamp = now > this._lastStamp ? now : this._lastStamp + 1;
        return this._lastStamp;
    }

    private observe(stamp: number): void {
        if (Number.isFinite(stamp) && stamp > this._lastStamp) this._lastStamp = stamp;
    }

    /** EVERY op of the session — retracted ones stay here so reinstating can
     * restore them; `log` exposes the active ones. Order: emission/arrival,
     * with reinstated ops MOVED to the tail (they are the newest edits). */
    private readonly _all: Operation[] = [];
    private readonly _retracted = new Set<string>();

    get log(): readonly Operation[] {
        return this._retracted.size === 0 ? this._all : this._all.filter(op => !this._retracted.has(op.id));
    }

    emit(payload: OperationPayload, origin: OperationOrigin = "local-user"): Operation {
        const sequence = this.stamp();
        const op = { ...payload, id: `op-${this._idNonce}-${++this._idCounter}`, sequence, origin } as Operation;
        this._all.push(op);
        this._onDidEmitOperation.fire(op);
        return op;
    }

    ingest(op: Operation): Operation {
        const existing = this._all.find(o => o.id === op.id);
        if (existing) return existing; // duplicate delivery — idempotent
        // the WIRE stamp is preserved — every window compares the same numbers
        // (the old local re-stamp made LWW per-window arrival order) — and the
        // clock bumps past it so later local edits outrank it
        this.observe(op.sequence);
        // `origin` becomes "remote" (what every consumer filters on), but the
        // SENDER's own origin is kept: attribution has to be able to say "the
        // AI did this" for an edit made in another client, and overwriting was
        // the only reason it could not.
        const ingested = { ...op, origin: "remote" as const, authoredBy: op.authoredBy ?? op.origin };
        this._all.push(ingested);
        this._onDidEmitOperation.fire(ingested);
        return ingested;
    }

    retract(ids: readonly string[]): void {
        const retracted: Operation[] = [];
        for (const id of ids) {
            if (this._retracted.has(id)) continue;
            const op = this._all.find(op => op.id === id);
            if (!op) continue; // unknown (e.g. remote)
            this._retracted.add(id);
            retracted.push(op);
        }
        if (retracted.length > 0) this._onDidChangeLog.fire({ retracted, reinstated: [] });
    }

    reinstate(ids: readonly string[], restamps?: Readonly<Record<string, number>>): void {
        const reinstated: Operation[] = [];
        for (const id of ids) {
            if (!this._retracted.delete(id)) continue;
            const index = this._all.findIndex(op => op.id === id);
            if (index < 0) continue;
            const op = this._all[index];
            // redo = RE-ASSERTION: fresh stamp (a peer's carried stamp verbatim,
            // so all windows renumber identically) + move to the log tail —
            // array order and stamp order stay aligned, and the fold agrees
            // with the live scene (the old original-position rule made a save
            // between redo and undo persist the outranked value)
            const stamp = restamps?.[id];
            if (stamp !== undefined) this.observe(stamp);
            (op as { sequence: number }).sequence = stamp ?? this.stamp();
            this._all.splice(index, 1);
            this._all.push(op);
            reinstated.push(op);
        }
        if (reinstated.length > 0) this._onDidChangeLog.fire({ retracted: [], reinstated });
    }

    get history(): readonly Operation[] {
        return this._all;
    }

    isRetracted(id: string): boolean {
        return this._retracted.has(id);
    }

    ingestRetracted(op: Operation): void {
        if (this._all.some(o => o.id === op.id)) return; // known — idempotent
        this.observe(op.sequence);
        this._all.push({ ...op, origin: "remote" });
        this._retracted.add(op.id);
    }
}
